/**
 * Company Brain — the per-account source of truth.
 *
 * One Brain per account, made of typed sections (identity, positioning,
 * ICP, goals, relationships, commercial, history, learned, rules). Every
 * other CRM agent reads context from the Brain and (in later phases)
 * writes learnings back to it. This file is the storage seam + the read
 * model; Phase 1 is read-only in the UI, so the only writers are the
 * mock seeder here and, later, the Orchestrator.
 *
 * The honesty contract (mirrors the validated argoiq-engine spec §3.3):
 *   - every fact carries source + confidence + evidence + who/when;
 *   - "unknown" is a first-class state (empty sections render honestly);
 *   - an agent never silently overwrites a higher-confidence fact with a
 *     lower-confidence one — it logs a `conflict` event instead;
 *   - facts are versioned, and `brainEvents` is an append-only history.
 *
 * Storage: two collections in the same typed `db` as everything else.
 * `brainFacts` holds the current "head" (one row per fact, deterministic
 * id so writes upsert); `brainEvents` is the append-only change log.
 */

import { db, nowIso, uuid } from '../db/store';
import { useFirestore } from '../firebase';
import { C } from '../tokens';
import type {
  Account,
  BrainEvent,
  BrainFact,
  BrainFactSource,
  BrainSection,
} from '../db/types';

// --- Section + source metadata (drives the read-only panel) ----------------

export interface BrainSectionMeta {
  id: BrainSection;
  label: string;
  blurb: string;
}

/** Canonical section order + copy. The panel renders sections in this order. */
export const BRAIN_SECTIONS: BrainSectionMeta[] = [
  { id: 'identity', label: 'Identity', blurb: 'Who they are — brand, domains, size, key people.' },
  { id: 'positioning', label: 'Positioning', blurb: 'Value prop, differentiators, competitors.' },
  { id: 'icp', label: 'ICP', blurb: 'Who they sell to — segments, personas, pains.' },
  { id: 'goals', label: 'Goals', blurb: "What they're trying to achieve." },
  { id: 'relationships', label: 'Relationships', blurb: 'POCs, champions, blockers, decision process.' },
  { id: 'commercial', label: 'Commercial', blurb: 'Deal size, pricing, renewal, deliverables, billing.' },
  { id: 'history', label: 'History', blurb: 'Key events, meetings, outcomes over time.' },
  { id: 'learned', label: 'Learned', blurb: "What's worked / not worked, with evidence." },
  { id: 'rules', label: 'Rules', blurb: 'Operator guardrails for this account.' },
];

const SECTION_LABEL: Record<BrainSection, string> = Object.fromEntries(
  BRAIN_SECTIONS.map((s) => [s.id, s.label]),
) as Record<BrainSection, string>;

export function sectionLabel(section: BrainSection): string {
  return SECTION_LABEL[section] ?? section;
}

export interface SourceMeta {
  label: string;
  color: string;
  /** Short hint shown in the provenance tooltip. */
  hint: string;
}

/**
 * Provenance chip styling per source. Founder-stated reads as the most
 * trusted (green); inferred (a model proposal) reads as the least (muted).
 */
export const SOURCE_META: Record<BrainFactSource, SourceMeta> = {
  founder: { label: 'Founder', color: C.green, hint: 'Stated by the customer directly.' },
  operator: { label: 'Operator', color: C.blueElectric, hint: 'Entered or confirmed by an Argo operator.' },
  research: { label: 'Research', color: C.magentaSoft, hint: 'Pulled from external research / enrichment.' },
  outcome: { label: 'Outcome', color: C.orange, hint: 'Derived from a real outcome.' },
  inferred: { label: 'Inferred', color: C.textMuted, hint: 'Proposed by a model — confirm to trust.' },
};

// --- Read model -------------------------------------------------------------

/** A section plus the facts currently held in it (newest-write first). */
export interface BrainSectionView {
  meta: BrainSectionMeta;
  facts: BrainFact[];
}

/** The whole Brain for one account: every section, empty ones included. */
export interface BrainView {
  accountId: string;
  sections: BrainSectionView[];
  /** Total fact count across all sections (0 = honest empty Brain). */
  factCount: number;
}

function groupIntoView(accountId: string, facts: BrainFact[]): BrainView {
  const mine = facts.filter((f) => f.accountId === accountId);
  const bySection = new Map<BrainSection, BrainFact[]>();
  for (const f of mine) {
    const arr = bySection.get(f.section) ?? [];
    arr.push(f);
    bySection.set(f.section, arr);
  }
  const sections: BrainSectionView[] = BRAIN_SECTIONS.map((meta) => ({
    meta,
    facts: (bySection.get(meta.id) ?? []).sort((a, b) =>
      a.setAt < b.setAt ? 1 : a.setAt > b.setAt ? -1 : 0,
    ),
  }));
  return { accountId, sections, factCount: mine.length };
}

/** One-shot read of an account's Brain, grouped into sections. */
export async function readBrain(accountId: string): Promise<BrainView> {
  const facts = await db.list('brainFacts');
  return groupIntoView(accountId, facts);
}

/**
 * Subscribe to an account's Brain. Re-runs the grouping on every change to
 * the `brainFacts` collection; returns an unsubscribe function.
 */
export function subscribeBrain(
  accountId: string,
  cb: (view: BrainView) => void,
): () => void {
  return db.subscribe('brainFacts', (facts) => cb(groupIntoView(accountId, facts)));
}

/**
 * Subscribe to the flat, all-accounts `brainFacts` head — no grouping, no
 * account filter. This exists for the chat's confirm-card diff: an
 * `update_brain` proposal can target *any* account, so the diff needs a live
 * snapshot of every fact keyed by its deterministic id to look up the current
 * value and show `label: old → new`. `subscribeBrain` can't serve this because
 * it groups into a single account's `BrainView`; the diff wants the raw rows.
 */
export function subscribeBrainFacts(
  cb: (facts: BrainFact[]) => void,
): () => void {
  return db.subscribe('brainFacts', cb);
}

/** Subscribe to an account's change log, newest-first. */
export function subscribeBrainEvents(
  accountId: string,
  cb: (events: BrainEvent[]) => void,
): () => void {
  return db.subscribe('brainEvents', (events) =>
    cb(
      events
        .filter((e) => e.accountId === accountId)
        .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0)),
    ),
  );
}

// --- Write path (used by the seeder now; the Orchestrator later) ------------

export interface PutFactInput {
  accountId: string;
  section: BrainSection;
  field: string;
  label: string;
  value: string;
  source: BrainFactSource;
  confidence: number;
  evidence?: string;
  setBy: string;
}

export interface PutFactResult {
  /** True when the fact was written; false when refused as a conflict. */
  written: boolean;
  fact: BrainFact;
  conflict?: boolean;
}

const factId = (accountId: string, section: BrainSection, field: string) =>
  `${accountId}:${section}:${field}`;

/**
 * Upsert a fact into the Brain. Bumps the version, appends a `brainEvents`
 * entry, and enforces the no-silent-downgrade rule: if a higher-confidence
 * fact already exists from a *different* source, the write is refused and a
 * `conflict` event is logged instead of overwriting it.
 */
export async function putBrainFact(input: PutFactInput): Promise<PutFactResult> {
  const id = factId(input.accountId, input.section, input.field);
  const existing = await db.get('brainFacts', id);
  const at = nowIso();

  // Honesty guard: never lower confidence by overwriting with a weaker,
  // differently-sourced claim. Surface it as a conflict to resolve.
  if (
    existing &&
    existing.source !== input.source &&
    existing.confidence > input.confidence
  ) {
    const conflictEvent: BrainEvent = {
      id: uuid(),
      accountId: input.accountId,
      section: input.section,
      field: input.field,
      oldValue: existing.value,
      newValue: input.value,
      source: input.source,
      agent: input.setBy,
      at,
      conflict: true,
    };
    await db.add('brainEvents', conflictEvent);
    return { written: false, fact: existing, conflict: true };
  }

  const fact: BrainFact = {
    id,
    accountId: input.accountId,
    section: input.section,
    field: input.field,
    label: input.label,
    value: input.value,
    source: input.source,
    confidence: clamp01(input.confidence),
    evidence: input.evidence,
    version: (existing?.version ?? 0) + 1,
    setBy: input.setBy,
    setAt: at,
  };
  await db.put('brainFacts', fact);

  const event: BrainEvent = {
    id: uuid(),
    accountId: input.accountId,
    section: input.section,
    field: input.field,
    oldValue: existing?.value,
    newValue: input.value,
    source: input.source,
    agent: input.setBy,
    at,
  };
  await db.add('brainEvents', event);

  return { written: true, fact };
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

// --- Undo support (used by the chat agent's confirm-each-write) -------------
// The chat agent proposes an operator Brain write behind a confirm card and
// needs a best-effort undo after it runs. Because the deterministic `factId`
// is module-private, the capture/restore/remove helpers live here rather than
// being reconstructed by the caller.

/** Read the current head fact for (account, section, field), if any. */
export async function getBrainFact(
  accountId: string,
  section: BrainSection,
  field: string,
): Promise<BrainFact | undefined> {
  return (await db.get('brainFacts', factId(accountId, section, field))) ?? undefined;
}

/**
 * Restore a previously-captured fact head verbatim and append a restore
 * entry to `brainEvents`. Used by undo so reverting an operator write puts
 * the exact prior value *and its provenance* back, rather than re-deriving
 * it (which would lose the original source/confidence).
 */
export async function restoreBrainFact(fact: BrainFact): Promise<void> {
  await db.put('brainFacts', fact);
  await db.add('brainEvents', {
    id: uuid(),
    accountId: fact.accountId,
    section: fact.section,
    field: fact.field,
    oldValue: undefined,
    newValue: fact.value,
    source: fact.source,
    agent: fact.setBy,
    at: nowIso(),
  });
}

/**
 * Remove the head fact for (account, section, field) and log the removal.
 * Used by undo when the operator write *created* a brand-new fact, so there
 * is no prior head to restore. No-op when the fact is already gone.
 */
export async function removeBrainFact(
  accountId: string,
  section: BrainSection,
  field: string,
): Promise<void> {
  const id = factId(accountId, section, field);
  const existing = await db.get('brainFacts', id);
  if (!existing) return;
  await db.remove('brainFacts', id);
  await db.add('brainEvents', {
    id: uuid(),
    accountId,
    section,
    field,
    oldValue: existing.value,
    newValue: '(removed)',
    source: existing.source,
    agent: 'operator',
    at: nowIso(),
  });
}

// --- Capture: deploy work → Brain ------------------------------------------

/**
 * Normalise a company name for fuzzy matching: lowercase, strip the common
 * legal suffixes + punctuation so "Acme, Inc." and "acme" resolve to the
 * same account.
 */
function normalizeCompany(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(inc|llc|ltd|corp|co|company|gmbh|plc)\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Resolve a company string to an account by normalised-name match. */
function matchAccountByName(accounts: Account[], company: string): Account | undefined {
  const want = normalizeCompany(company);
  if (!want) return undefined;
  // Exact normalised match first, then a containment fallback so
  // "Drift Peptides Labs" still finds the "Drift Peptides" account.
  return (
    accounts.find((a) => normalizeCompany(a.name) === want) ??
    accounts.find((a) => {
      const got = normalizeCompany(a.name);
      return got.length > 2 && (got.includes(want) || want.includes(got));
    })
  );
}

export interface CaptureEnrichmentInput {
  /** The company the prospect belongs to — used to resolve the account. */
  company?: string;
  /** The prospect the read came from (cited as evidence). */
  personName: string;
  summary?: string;
  signals?: string[];
  /** 0–100 fit score; drives the fact confidence when present. */
  fitScore?: number;
  /** Attribution for the change log, e.g. 'Enrichment worker'. */
  setBy: string;
}

export interface CaptureEnrichmentResult {
  written: boolean;
  /** The account the facts landed on, when a match was found. */
  accountId?: string;
}

/**
 * Pipe an enrichment read into the matching account's Brain as
 * `research`-sourced facts (the summary into Positioning, the signals into
 * ICP). No-op when the prospect's company doesn't map to a known account —
 * we never invent an account, and the conflict guard in `putBrainFact`
 * still protects any higher-confidence founder/operator facts. This is the
 * Phase-2 "deploy capture → Brain" seam: the Brain fills as the system
 * actually does work, not just from the seed.
 */
export async function captureEnrichmentToBrain(
  input: CaptureEnrichmentInput,
): Promise<CaptureEnrichmentResult> {
  if (!input.company) return { written: false };
  const accounts = await db.list('accounts');
  const account = matchAccountByName(accounts, input.company);
  if (!account) return { written: false };

  // Map the 0–100 fit score into a modest research-band confidence so an
  // automated read never out-ranks a stated fact. Default 0.6 when absent.
  const confidence =
    input.fitScore !== undefined
      ? clamp01(0.4 + (input.fitScore / 100) * 0.4)
      : 0.6;
  const evidence = `Enrichment read via ${input.personName}`;

  let wrote = false;
  if (input.summary) {
    const res = await putBrainFact({
      accountId: account.id,
      section: 'positioning',
      field: 'researchRead',
      label: 'Research read',
      value: input.summary,
      source: 'research',
      confidence,
      evidence,
      setBy: input.setBy,
    });
    wrote = wrote || res.written;
  }
  if (input.signals && input.signals.length > 0) {
    const res = await putBrainFact({
      accountId: account.id,
      section: 'icp',
      field: 'enrichmentSignals',
      label: 'Enrichment signals',
      value: input.signals.join(' · '),
      source: 'research',
      confidence: clamp01(confidence - 0.1),
      evidence,
      setBy: input.setBy,
    });
    wrote = wrote || res.written;
  }

  return { written: wrote, accountId: account.id };
}

// --- Mock-only seed ---------------------------------------------------------

/**
 * Seed a believable Brain for the first couple of accounts so the panel is
 * rich in demo. Mock-mode ONLY: gated on `!useFirestore` exactly like the
 * demo CRM roster, so the live Firestore path is never polluted and the
 * Brain starts honestly empty in production. Idempotent — only runs when
 * `brainFacts` is empty.
 */
export async function bootstrapBrainIfEmpty(): Promise<void> {
  if (useFirestore) return; // production starts empty + honest
  const existing = await db.list('brainFacts');
  if (existing.length > 0) return;

  const accounts = await db.list('accounts');
  if (accounts.length === 0) return;

  // Seed the first two accounts with a spread of sections + sources so the
  // provenance chips and empty states are both visible in the demo.
  const targets = accounts.slice(0, 2);
  for (let i = 0; i < targets.length; i += 1) {
    await seedOneAccount(targets[i], i === 0);
  }
}

async function seedOneAccount(account: Account, rich: boolean): Promise<void> {
  const owner = account.ownerName || 'Argo';
  const poc = account.pocName || 'their primary contact';
  const facts: Omit<PutFactInput, 'accountId'>[] = [
    {
      section: 'identity',
      field: 'brandName',
      label: 'Brand name',
      value: account.name,
      source: 'founder',
      confidence: 1,
      setBy: 'operator',
    },
    {
      section: 'relationships',
      field: 'primaryPoc',
      label: 'Primary POC',
      value: account.pocTitle ? `${poc} — ${account.pocTitle}` : poc,
      source: 'operator',
      confidence: 0.95,
      setBy: owner,
      evidence: account.pocEmail || undefined,
    },
    {
      section: 'goals',
      field: 'primaryGoal',
      label: 'Primary goal',
      value: 'Grow qualified pipeline and shorten time-to-close.',
      source: 'inferred',
      confidence: 0.45,
      setBy: 'icp-builder',
    },
  ];

  if (rich) {
    facts.push(
      {
        section: 'positioning',
        field: 'valueProp',
        label: 'Value proposition',
        value: 'Trusted, AI-assisted operations for mid-market teams.',
        source: 'research',
        confidence: 0.7,
        setBy: 'market-research',
        evidence: 'web: about page',
      },
      {
        section: 'icp',
        field: 'segment',
        label: 'Target segment',
        value: 'B2B mid-market (50–500 staff), ops-led buying.',
        source: 'inferred',
        confidence: 0.5,
        setBy: 'icp-builder',
      },
      {
        section: 'commercial',
        field: 'dealBand',
        label: 'Deal band',
        value: 'Mid five-figure ARR, annual.',
        source: 'inferred',
        confidence: 0.4,
        setBy: 'orchestrator',
      },
      {
        section: 'learned',
        field: 'channelSignal',
        label: 'What worked',
        value: 'Warm intros convert ~3x faster than cold outreach here.',
        source: 'outcome',
        confidence: 0.65,
        setBy: 'evaluate',
        evidence: 'win-loss: 2 closed-won',
      },
      {
        section: 'rules',
        field: 'approval',
        label: 'Guardrail',
        value: 'Always human-approve outbound to this account.',
        source: 'operator',
        confidence: 1,
        setBy: owner,
      },
    );
  }

  for (const f of facts) {
    await putBrainFact({ ...f, accountId: account.id });
  }
}
